/**
 * Booking Audit (Epic 18, story 18-8).
 *
 * Two views over the same cross-user booking feed, switchable via tabs
 * (Google-Sheets style):
 *  - Technical  — per-booking agent telemetry (events · tools · screenshots ·
 *                 tokens · wall time), grouped per studio so slow/failing
 *                 studios stand out. (The original audit view.)
 *  - Business   — every booking flat in chronological order, led by business
 *                 metrics (volume, reach, success rate) for a non-technical
 *                 read on how booking is performing.
 */
import { useEffect, useMemo, useState } from "react"
import { RefreshCw, ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react"
import { BACKEND_API_URL, authHeaders } from "@/lib/api"
import { cn } from "@/lib/utils"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"

interface BookingTelemetry {
  events?: number
  tool_calls?: number
  screenshots?: number
  iterations?: number
  duration_ms?: number
  input_tokens?: number
  output_tokens?: number
  model?: string
}

interface AuditBooking {
  id: string
  user_id: string
  user_name?: string
  is_team?: boolean
  studio_id: string
  studio_name: string
  status: string
  created_at: string
  booked_at?: string | null
  booked_class?: string
  input_type?: string
  telemetry?: BookingTelemetry | null
}

const STATUS_TONE: Record<string, string> = {
  confirmed: "text-emerald-600",
  needs_user: "text-amber-600",
  in_progress: "text-sky-600",
  pending: "text-sky-600",
  failed: "text-red-600",
  timeout: "text-red-600",
  cancelled: "text-muted-foreground",
}

// Outcomes that represent a finished booking attempt (used for success rate —
// in_progress / pending / needs_user are still open, so they don't count as
// failures against the rate).
const RESOLVED_STATUSES = ["confirmed", "failed", "timeout", "cancelled"] as const

// Sortable columns in the Business-view flat table.
type BizSortKey =
  | "when"
  | "studio"
  | "status"
  | "class"
  | "trigger"
  | "bookTime"
  | "user"

function fmtDuration(ms?: number): string {
  if (!ms) return "—"
  const s = Math.round(ms / 1000)
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`
}

const INITIAL_LIMIT = 150 // first page size
const PAGE_SIZE = 50 // "load more" increment

export function BookingAuditPage() {
  const [bookings, setBookings] = useState<AuditBooking[]>([])
  const [total, setTotal] = useState(0) // absolute all-time count from the backend
  // All-time count per status, straight from the DB — so the outcome cards read
  // the same whether 150 rows are loaded or all of them.
  const [statusBreakdown, setStatusBreakdown] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch one page. offset=0 replaces the list (initial / refresh); a non-zero
  // offset appends the next slice. `total` always reflects the all-time count.
  const fetchPage = async (offset: number, limit: number) => {
    const res = await fetch(
      `${BACKEND_API_URL}/admin/booking_audit?limit=${limit}&offset=${offset}`,
      { headers: await authHeaders() },
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return (await res.json()) as {
      bookings: AuditBooking[]
      total: number
      status_breakdown?: Record<string, number>
    }
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchPage(0, INITIAL_LIMIT)
      setBookings(data.bookings ?? [])
      setTotal(data.total ?? 0)
      setStatusBreakdown(data.status_breakdown ?? {})
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load booking audit")
    } finally {
      setLoading(false)
    }
  }

  const loadMore = async () => {
    setLoadingMore(true)
    setError(null)
    try {
      const data = await fetchPage(bookings.length, PAGE_SIZE)
      setBookings(prev => [...prev, ...(data.bookings ?? [])])
      setTotal(data.total ?? total)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more bookings")
    } finally {
      setLoadingMore(false)
    }
  }

  const hasMore = bookings.length < total

  useEffect(() => {
    void load()
  }, [])

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Booking Audit</h1>
          <p className="text-sm text-muted-foreground">
            Cross-user booking feed. Telemetry appears on bookings created after
            the Epic 18 deploy.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <Tabs defaultValue="technical">
        <TabsList>
          <TabsTrigger value="technical">Technical</TabsTrigger>
          <TabsTrigger value="business">Business</TabsTrigger>
        </TabsList>

        <TabsContent value="technical">
          <TechnicalView bookings={bookings} loading={loading} error={error} />
        </TabsContent>

        <TabsContent value="business">
          <BusinessView
            bookings={bookings}
            total={total}
            statusBreakdown={statusBreakdown}
            loading={loading}
            error={error}
          />
        </TabsContent>
      </Tabs>

      {/* Shared paging footer — both views read the same loaded rows. */}
      {bookings.length > 0 && (
        <div className="flex items-center justify-center gap-3 pt-2 text-sm text-muted-foreground">
          <span>
            Loaded {bookings.length} of {total}
          </span>
          {hasMore && (
            <button
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 hover:bg-accent disabled:opacity-50"
            >
              {loadingMore && <RefreshCw className="h-4 w-4 animate-spin" />}
              Load {Math.min(PAGE_SIZE, total - bookings.length)} more
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// --- Technical view (original: per-studio telemetry) -------------------------

function TechnicalView({
  bookings,
  loading,
  error,
}: {
  bookings: AuditBooking[]
  loading: boolean
  error: string | null
}) {
  const byStudio = useMemo(() => {
    const groups = new Map<string, AuditBooking[]>()
    for (const b of bookings) {
      const key = b.studio_name || b.studio_id
      const list = groups.get(key) ?? []
      list.push(b)
      groups.set(key, list)
    }
    // Studios with the most recent activity first.
    return [...groups.entries()].sort(
      (a, b) =>
        (b[1][0]?.created_at ?? "").localeCompare(a[1][0]?.created_at ?? ""),
    )
  }, [bookings])

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Per-booking agent telemetry (events · tools · screenshots · tokens ·
        wall time), grouped by studio so slow or failing studios stand out.
      </p>

      {byStudio.map(([studio, rows]) => (
        <div key={studio} className="rounded-lg border">
          <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2">
            <span className="font-medium">{studio}</span>
            <span className="text-xs text-muted-foreground">
              {rows.length} booking{rows.length === 1 ? "" : "s"}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-4 py-2">When</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Class</th>
                <th className="px-2 py-2">Pause type</th>
                <th className="px-2 py-2 text-right">Wall</th>
                <th className="px-2 py-2 text-right">Iter</th>
                <th className="px-2 py-2 text-right">Tools</th>
                <th className="px-2 py-2 text-right">Shots</th>
                <th className="px-2 py-2 text-right">Tokens in/out</th>
                <th className="px-4 py-2">Model</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(b => {
                const t = b.telemetry
                return (
                  <tr key={b.id} className="border-b last:border-0">
                    <td className="px-4 py-1.5 text-xs">
                      {new Date(b.created_at).toLocaleString()}
                    </td>
                    <td
                      className={cn(
                        "px-2 py-1.5 font-medium",
                        STATUS_TONE[b.status] ?? "",
                      )}
                    >
                      {b.status}
                    </td>
                    <td className="px-2 py-1.5">{b.booked_class || "—"}</td>
                    <td className="px-2 py-1.5">{b.input_type || "—"}</td>
                    <td className="px-2 py-1.5 text-right">
                      {fmtDuration(t?.duration_ms)}
                    </td>
                    <td className="px-2 py-1.5 text-right">{t?.iterations ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right">{t?.tool_calls ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right">{t?.screenshots ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right">
                      {t?.input_tokens != null
                        ? `${t.input_tokens}/${t.output_tokens ?? 0}`
                        : "—"}
                    </td>
                    <td className="px-4 py-1.5 text-xs">{t?.model || "—"}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ))}

      {!loading && bookings.length === 0 && !error && (
        <p className="text-sm text-muted-foreground">No bookings recorded yet.</p>
      )}
    </div>
  )
}

// --- Business view (flat, chronological, metrics-led) ------------------------

function BusinessView({
  bookings,
  total,
  statusBreakdown,
  loading,
  error,
}: {
  bookings: AuditBooking[]
  total: number // absolute all-time booking count
  statusBreakdown: Record<string, number> // all-time count per status
  loading: boolean
  error: string | null
}) {
  // Every column is a clickable sort. Default: When, newest-first.
  const [sortKey, setSortKey] = useState<BizSortKey>("when")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  function toggleSort(key: BizSortKey) {
    if (key === sortKey) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      // Time + duration read more naturally biggest-first; text columns A→Z.
      setSortDir(key === "when" || key === "bookTime" ? "desc" : "asc")
    }
  }

  const metrics = useMemo(() => {
    const loadedCount = bookings.length
    const studioIds = new Set<string>()
    const userIds = new Set<string>()
    const studioCounts = new Map<string, number>()
    let bookTimeSum = 0
    let bookTimeN = 0

    // Reach and timing stats can only be read off the rows we hold.
    for (const b of bookings) {
      studioIds.add(b.studio_id || b.studio_name)
      userIds.add(b.user_id)
      if (b.status === "confirmed") {
        const key = b.studio_name || b.studio_id
        studioCounts.set(key, (studioCounts.get(key) ?? 0) + 1)
        const ms = b.telemetry?.duration_ms
        if (ms) {
          bookTimeSum += ms
          bookTimeN += 1
        }
      }
    }

    // Outcome counts come from the backend's all-time tally so they don't creep
    // upward as more pages load. Older backends omit it — fall back to counting
    // the rows in hand rather than showing zeros.
    const allTime = Object.keys(statusBreakdown).length > 0
    const tally = (...statuses: string[]) =>
      allTime
        ? statuses.reduce((sum, s) => sum + (statusBreakdown[s] ?? 0), 0)
        : bookings.filter(b => statuses.includes(b.status)).length

    // Timeouts are failures from the member's point of view — the agent never
    // got them a spot — so they sit with `failed` rather than in their own card.
    const confirmed = tally("confirmed")
    const failed = tally("failed", "timeout")
    const cancelled = tally("cancelled")
    const needsUser = tally("needs_user")
    const resolved = tally(...RESOLVED_STATUSES)

    const topStudio = [...studioCounts.entries()].sort((a, b) => b[1] - a[1])[0]

    return {
      loadedCount,
      allTime,
      studiosBooked: studioIds.size,
      uniqueUsers: userIds.size,
      confirmed,
      failed,
      cancelled,
      successRate: resolved ? Math.round((confirmed / resolved) * 100) : null,
      needsUser,
      avgBookMs: bookTimeN ? bookTimeSum / bookTimeN : undefined,
      topStudio: topStudio ? { name: topStudio[0], count: topStudio[1] } : null,
    }
  }, [bookings, statusBreakdown])

  // Every status broken down by who booked it - team (internal
  // @offseasonwellness.com accounts) vs. members. Nothing here is filtered out:
  // a team booking runs the exact same pipeline as a member's, so a confirmed/
  // cancelled/failed row from the team is just as real a proof point as one
  // from a member, not noise to hide. The four core outcomes always render,
  // even at zero - a missing row reads as "not tracked", a zero row reads as
  // "tracked, currently zero", and those are very different signals here.
  const bySource = useMemo(() => {
    const map = new Map<string, { team: number; member: number }>()
    for (const status of ["confirmed", "failed", "cancelled", "needs_user"]) {
      map.set(status, { team: 0, member: 0 })
    }
    for (const b of bookings) {
      const entry = map.get(b.status) ?? { team: 0, member: 0 }
      if (b.is_team) entry.team += 1
      else entry.member += 1
      map.set(b.status, entry)
    }
    return [...map.entries()]
      .map(([status, counts]) => ({ status, ...counts, total: counts.team + counts.member }))
      .sort((a, b) => b.total - a.total)
  }, [bookings])

  const ordered = useMemo(() => {
    const valueOf = (b: AuditBooking): string | number => {
      switch (sortKey) {
        case "when":
          return b.created_at
        case "studio":
          return (b.studio_name || b.studio_id).toLowerCase()
        case "status":
          return b.status.toLowerCase()
        case "class":
          return (b.booked_class ?? "").toLowerCase()
        case "trigger":
          return (b.input_type ?? "").toLowerCase()
        case "bookTime":
          return b.telemetry?.duration_ms ?? -1
        case "user":
          return (b.user_name || b.user_id).toLowerCase()
      }
    }
    const dir = sortDir === "asc" ? 1 : -1
    return [...bookings].sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      let cmp: number
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv
      } else {
        cmp = String(av).localeCompare(String(bv))
      }
      // Stable tiebreak by time so same-value rows keep a sensible order.
      if (cmp === 0) cmp = a.created_at.localeCompare(b.created_at)
      return cmp * dir
    })
  }, [bookings, sortKey, sortDir])

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Every booking in one chronological feed (no studio grouping), with the
        business-level health of the booking funnel up top.
      </p>

      {/* Outcome cards — every finished attempt lands in exactly one of
          Successful / Failed / Cancelled, so the three read as a set. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Metric label="Total bookings" value={total} hint="all-time" />
        <Metric
          label="Successful"
          value={metrics.confirmed}
          tone="text-emerald-600"
          hint="agent confirmed a spot"
        />
        <Metric
          label="Failed"
          value={metrics.failed}
          tone={metrics.failed > 0 ? "text-red-600" : undefined}
          hint="incl. timeouts"
        />
        <Metric
          label="Cancelled"
          value={metrics.cancelled}
          hint="called off before booking"
        />
        <Metric
          label="Success rate"
          value={metrics.successRate == null ? "—" : `${metrics.successRate}%`}
          hint="successful ÷ resolved attempts"
        />
        <Metric
          label="Needs attention"
          value={metrics.needsUser}
          tone={metrics.needsUser > 0 ? "text-amber-600" : undefined}
          hint="paused on needs_user"
        />
        <Metric label="Studios booked" value={metrics.studiosBooked} />
        <Metric label="Unique users" value={metrics.uniqueUsers} />
      </div>

      {metrics.loadedCount < total && (
        <p className="text-xs text-muted-foreground">
          {metrics.allTime
            ? "Outcome counts and success rate are all-time, straight from the database."
            : "Outcome counts cover only the bookings loaded so far (the backend is not yet reporting all-time totals)."}{" "}
          Studios booked, unique users, avg time to book and most-booked studio
          are based on the {metrics.loadedCount} bookings loaded — load more
          below to widen that sample.
        </p>
      )}

      {/* Secondary insights */}
      <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm text-muted-foreground">
        <span>
          Avg time to book (confirmed):{" "}
          <span className="font-medium text-foreground">
            {fmtDuration(metrics.avgBookMs)}
          </span>
        </span>
        <span>
          Most-booked studio:{" "}
          <span className="font-medium text-foreground">
            {metrics.topStudio
              ? `${metrics.topStudio.name} (${metrics.topStudio.count})`
              : "—"}
          </span>
        </span>
      </div>

      {/* By source: team vs. members, per status. Nothing is excluded here -
          a team-account booking runs through the identical pipeline as a
          member's, so its outcome is real evidence the pipeline works. */}
      {bySource.length > 0 && (
        <div className="rounded-lg border">
          <div className="border-b bg-muted/40 px-4 py-2">
            <span className="font-medium">By source</span>
            <span className="ml-2 text-xs text-muted-foreground">
              Team (@offseasonwellness.com) bookings run the same pipeline as
              users' - included here, not filtered out.
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-4 py-2">Status</th>
                <th className="px-2 py-2 text-right">Team</th>
                <th className="px-2 py-2 text-right">Users</th>
                <th className="px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {bySource.map(row => (
                <tr key={row.status} className="border-b last:border-0">
                  <td
                    className={cn(
                      "px-4 py-1.5 font-medium",
                      STATUS_TONE[row.status] ?? "",
                    )}
                  >
                    {row.status}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.team}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.member}</td>
                  <td className="px-4 py-1.5 text-right font-medium tabular-nums">{row.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Flat chronological table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
              <BizSortHeader label="When" col="when" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="px-4 py-2" />
              <BizSortHeader label="Studio" col="studio" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <BizSortHeader label="Status" col="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <BizSortHeader label="Class" col="class" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <BizSortHeader label="Trigger" col="trigger" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <BizSortHeader label="Book time" col="bookTime" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <BizSortHeader label="User" col="user" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {ordered.map(b => (
              <tr key={b.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="px-4 py-1.5 text-xs">
                  {new Date(b.created_at).toLocaleString()}
                </td>
                <td className="px-2 py-1.5 font-medium">
                  {b.studio_name || b.studio_id}
                </td>
                <td
                  className={cn(
                    "px-2 py-1.5 font-medium",
                    STATUS_TONE[b.status] ?? "",
                  )}
                >
                  {b.status}
                </td>
                <td className="px-2 py-1.5">{b.booked_class || "—"}</td>
                <td className="px-2 py-1.5">{b.input_type || "—"}</td>
                <td className="px-2 py-1.5 text-right">
                  {fmtDuration(b.telemetry?.duration_ms)}
                </td>
                <td className="px-4 py-1.5">
                  {b.user_name || b.user_id.slice(0, 8)}
                  {b.is_team && (
                    <span className="ml-1.5 rounded border border-sky-200 bg-sky-50 px-1 py-0.5 text-[10px] font-medium text-sky-700">
                      Team
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && bookings.length === 0 && !error && (
        <p className="text-sm text-muted-foreground">No bookings recorded yet.</p>
      )}
    </div>
  )
}

function BizSortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
  align,
  className,
}: {
  label: string
  col: BizSortKey
  sortKey: BizSortKey
  sortDir: "asc" | "desc"
  onSort: (k: BizSortKey) => void
  align?: "right"
  className?: string
}) {
  const active = sortKey === col
  return (
    <th className={cn("px-2 py-2", align === "right" && "text-right", className)}>
      <button
        onClick={() => onSort(col)}
        className={cn(
          "inline-flex items-center gap-1 rounded hover:text-foreground",
          align === "right" && "flex-row-reverse",
          active && "text-foreground",
        )}
        title={`Sort by ${label}`}
      >
        {label}
        {active ? (
          sortDir === "asc" ? (
            <ChevronUp className="size-3.5" />
          ) : (
            <ChevronDown className="size-3.5" />
          )
        ) : (
          <ChevronsUpDown className="size-3.5 opacity-40" />
        )}
      </button>
    </th>
  )
}

function Metric({
  label,
  value,
  tone,
  hint,
}: {
  label: string
  value: number | string
  tone?: string
  hint?: string
}) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className={cn("text-2xl font-bold tabular-nums", tone)}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {hint && <div className="mt-0.5 text-[10px] text-muted-foreground/70">{hint}</div>}
    </div>
  )
}
